/**
 * FilesCell Component
 *
 * Renders file attachments with preview and upload capability.
 */

import { useState, useRef, Fragment } from 'react';
import { Transition } from '@headlessui/react';
import {
  PaperClipIcon,
  DocumentIcon,
  PhotoIcon,
  FilmIcon,
  MusicalNoteIcon,
  TrashIcon,
  ArrowUpTrayIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { FilesCellProps } from './types';

// File type icon mapping
const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return PhotoIcon;
  if (type.startsWith('video/')) return FilmIcon;
  if (type.startsWith('audio/')) return MusicalNoteIcon;
  return DocumentIcon;
};

// Format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export function FilesCell({
  value,
  onChange,
  files = [],
  onUpload,
  onRemove,
  readonly = false,
}: FilesCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fileIds = value?.fileIds ?? [];

  // Get attached files details
  const attachedFiles = fileIds
    .map(id => files.find(f => f.id === id))
    .filter(Boolean) as NonNullable<typeof files[0]>[];

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (selectedFiles && onUpload) {
      onUpload(Array.from(selectedFiles));
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragging(false);

    if (readonly || !onUpload) return;

    const droppedFiles = event.dataTransfer.files;
    if (droppedFiles.length > 0) {
      onUpload(Array.from(droppedFiles));
    }
  };

  const handleRemoveFile = (fileId: string) => {
    if (readonly || !onChange) return;

    // Update the cell value
    const newIds = fileIds.filter(id => id !== fileId);
    onChange(newIds.length > 0 ? { fileIds: newIds } : null);

    // Also call the onRemove callback if provided
    onRemove?.(fileId);
  };

  if (readonly) {
    return (
      <div className="w-full h-full flex items-center">
        {attachedFiles.length > 0 ? (
          <div className="flex items-center gap-1">
            <PaperClipIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-700">
              {attachedFiles.length} file{attachedFiles.length !== 1 ? 's' : ''}
            </span>
          </div>
        ) : fileIds.length > 0 ? (
          // Files exist but no metadata
          <div className="flex items-center gap-1">
            <PaperClipIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-700">
              {fileIds.length} file{fileIds.length !== 1 ? 's' : ''}
            </span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <button
        onClick={() => setIsOpen(true)}
        className="w-full h-full flex items-center cursor-pointer min-h-[28px]"
      >
        {fileIds.length > 0 ? (
          <div className="flex items-center gap-1">
            <PaperClipIcon className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-700">
              {fileIds.length} file{fileIds.length !== 1 ? 's' : ''}
            </span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm hover:text-gray-500">-</span>
        )}
      </button>

      <Transition
        show={isOpen}
        as={Fragment}
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="absolute z-50 mt-1 bg-white rounded-lg shadow-dropdown border border-surface-border w-72 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-surface-border">
            <span className="text-sm font-medium text-gray-900">Files</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {/* Upload area */}
          <div
            className={`
              p-4 border-2 border-dashed m-3 rounded-lg transition-colors
              ${isDragging ? 'border-primary-500 bg-primary-50' : 'border-gray-200'}
            `}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="text-center">
              <ArrowUpTrayIcon className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-1 text-sm text-gray-600">
                Drag files here or{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-primary-600 hover:text-primary-700"
                >
                  browse
                </button>
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Max 25MB per file
              </p>
            </div>
          </div>

          {/* File list */}
          {attachedFiles.length > 0 && (
            <div className="px-3 pb-3">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Attached files
              </div>
              <div className="space-y-2 max-h-48 overflow-auto">
                {attachedFiles.map((file) => {
                  const FileIcon = getFileIcon(file.type);
                  return (
                    <div
                      key={file.id}
                      className="flex items-center gap-2 p-2 bg-surface-secondary rounded-lg group"
                    >
                      {file.thumbnailUrl ? (
                        <img
                          src={file.thumbnailUrl}
                          alt={file.name}
                          className="h-8 w-8 rounded object-cover"
                        />
                      ) : (
                        <FileIcon className="h-8 w-8 text-gray-400" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveFile(file.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Show file IDs if no metadata available */}
          {attachedFiles.length === 0 && fileIds.length > 0 && (
            <div className="px-3 pb-3">
              <div className="text-xs font-medium text-gray-500 mb-2">
                Attached files ({fileIds.length})
              </div>
              <div className="space-y-2 max-h-48 overflow-auto">
                {fileIds.map((fileId) => (
                  <div
                    key={fileId}
                    className="flex items-center gap-2 p-2 bg-surface-secondary rounded-lg group"
                  >
                    <DocumentIcon className="h-8 w-8 text-gray-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-500 truncate">
                        {fileId}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemoveFile(fileId)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-opacity"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Transition>
    </div>
  );
}
